// services/captcha/adapters/arcaptcha.ts
//
// Provider `arcaptcha`. Facts from captcha-client-integration.md §6:
// - the site key only works on domains registered for it (rabex.ir and
//   www.rabex.ir); anywhere else the widget fails with error 101;
// - `execute()` resolves "" on failure instead of rejecting;
//
// And from the script itself (nwidget.arcaptcha.ir/1/api.js), where the doc
// is wrong:
// - `render()` returns the id at once but builds the widget only after it has
//   fetched the site key's methods; `execute`/`reset` work from
//   `rendered_callback` on, and before that `reset` is not even defined;
// - the callback options are snake_case (`error_callback`, ...); the doc's
//   `'error-callback'` is ignored;
// - `closed_callback` fires when the user closes the puzzle.
// - automated browsers always get a visual puzzle, so the container must stay
//   visible;
// - the widget talks to nwidget., widget., bowman. and api.arcaptcha.ir.

import { CaptchaAdapter, CaptchaError } from '../types';

// Fixed here on purpose: nothing from a response is ever used to load code.
const SCRIPT_URL = 'https://nwidget.arcaptcha.ir/1/api.js';

type WidgetId = string | number;

type ArcaptchaApi = {
  render: (container: HTMLElement, params: Record<string, unknown>) => WidgetId;
  // Optional: added by the script only once the widget's methods are loaded.
  execute?: (id: WidgetId) => Promise<string> | undefined;
  reset?: (id: WidgetId) => void;
};

declare global {
  interface Window {
    arcaptcha?: ArcaptchaApi;
  }
}

let loading: Promise<ArcaptchaApi> | null = null;

// The widget's stylesheets declare their own `Yekan` font, the same family
// name as the app's (YekanBakh FaNum). Added after ours, they would win for
// every weight they declare and change the font of the whole app. So each
// time the widget adds a stylesheet, a copy of the app's own `Yekan`
// @font-face rules goes in after it: the last declaration wins again.
const FONT_GUARD_ID = 'app-yekan-font-faces';
const GUARDED_FAMILY = 'Yekan';

const isProviderSheet = (node: Node): boolean =>
  (node instanceof HTMLLinkElement && node.href.includes('arcaptcha')) ||
  (node instanceof HTMLStyleElement &&
    node.id !== FONT_GUARD_ID &&
    (node.textContent ?? '').includes('arcaptcha'));

const numericWeight = (value: string): number => {
  if (value === 'bold') return 700;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 400 : n;
};

/**
 * The face the browser picks for `desired` among `available` (CSS Fonts
 * matching): 400–500 look up to 500, then down, then up; lighter look down
 * first; heavier look up first.
 */
export const matchWeight = (desired: number, available: number[]): number => {
  const asc = [...available].sort((x, y) => x - y);
  const below = asc.filter((w) => w < desired).reverse();
  const above = asc.filter((w) => w > desired);
  if (asc.includes(desired)) return desired;
  if (desired >= 400 && desired <= 500) {
    const upTo500 = above.filter((w) => w <= 500);
    return upTo500[0] ?? below[0] ?? above[0];
  }
  return desired < 400 ? below[0] ?? above[0] : above[0] ?? below[0];
};

/**
 * The app's own @font-face rules for the guarded family, urls made absolute,
 * plus a copy for every standard weight the app lacks, pointing at the face
 * the browser chose before: the widget declares 100–800, and a weight only it
 * has (500, used for MUI's medium) would otherwise come from its files.
 */
const appFontFaces = (): string => {
  const faces = new Map<number, string>();
  Array.from(document.styleSheets).forEach((sheet) => {
    const owner = sheet.ownerNode;
    if (!owner || isProviderSheet(owner)) return;
    if (owner instanceof HTMLStyleElement && owner.id === FONT_GUARD_ID) return;
    let list: CSSRuleList;
    try {
      list = sheet.cssRules;
    } catch {
      return; // another origin's sheet: not ours
    }
    Array.from(list).forEach((rule) => {
      if (!(rule instanceof CSSFontFaceRule)) return;
      const family = rule.style
        .getPropertyValue('font-family')
        .replace(/["']/g, '')
        .trim();
      if (family !== GUARDED_FAMILY) return;
      const base = sheet.href ?? document.baseURI;
      faces.set(
        numericWeight(rule.style.getPropertyValue('font-weight')),
        rule.cssText.replace(
          /url\((['"]?)([^'")]+)\1\)/g,
          (_m, q, url) => `url(${q}${new URL(url, base).href}${q})`
        )
      );
    });
  });
  if (!faces.size) return '';

  const available = Array.from(faces.keys());
  const rules = Array.from(faces.values());
  for (let weight = 100; weight <= 900; weight += 100) {
    if (!faces.has(weight)) {
      rules.push(
        faces
          .get(matchWeight(weight, available))!
          .replace(/font-weight:\s*[^;]+;/, `font-weight: ${weight};`)
      );
    }
  }
  return rules.join('\n');
};

let fontGuardStarted = false;

const startFontGuard = () => {
  if (fontGuardStarted) return;
  fontGuardStarted = true;
  const css = appFontFaces();
  if (!css) return;
  const reassert = () => {
    let guard = document.getElementById(FONT_GUARD_ID);
    if (!guard) {
      guard = document.createElement('style');
      guard.id = FONT_GUARD_ID;
      guard.textContent = css;
    }
    // Appending an existing node moves it: ours ends up last again.
    document.head.appendChild(guard);
  };
  new MutationObserver((mutations) => {
    if (mutations.some((m) => Array.from(m.addedNodes).some(isProviderSheet))) {
      reassert();
    }
  }).observe(document.head, { childList: true });
  // In case the widget's sheets are already in.
  if (Array.from(document.head.children).some(isProviderSheet)) reassert();
};

const loadScript = (): Promise<ArcaptchaApi> => {
  startFontGuard();
  if (window.arcaptcha) return Promise.resolve(window.arcaptcha);
  if (loading) return loading;

  loading = new Promise<ArcaptchaApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () =>
      window.arcaptcha
        ? resolve(window.arcaptcha)
        : reject(new CaptchaError('widget', 'script loaded without its API'));
    script.onerror = () => {
      // Let the next challenge try again instead of reusing this failure.
      loading = null;
      script.remove();
      reject(new CaptchaError('widget', 'script failed to load'));
    };
    document.head.appendChild(script);
  });
  return loading;
};

export const createArcaptchaAdapter = (): CaptchaAdapter => {
  let widgetId: WidgetId | undefined;

  return {
    async solve(container, siteKey, options) {
      const arcaptcha = await loadScript();
      const size = options.size === 'normal' ? 'normal' : 'invisible';

      return new Promise<string>((resolve, reject) => {
        let settled = false;
        const fail = (
          detail?: unknown,
          reason: 'widget' | 'cancelled' = 'widget'
        ) => {
          if (settled) return;
          settled = true;
          reject(new CaptchaError(reason, String(detail ?? '')));
        };
        const succeed = (token: unknown) => {
          if (settled) return;
          // An empty token is a failure, not a token.
          if (typeof token !== 'string' || !token) {
            fail('empty token');
            return;
          }
          settled = true;
          resolve(token);
        };

        const execute = () => {
          if (widgetId === undefined) return;
          try {
            // The token normally arrives through `callback`; the promise is
            // only read for its failure ("").
            const answer = arcaptcha.execute?.(widgetId);
            answer?.then((token) => {
              if (!token) fail('empty token');
            }, fail);
          } catch (e) {
            fail(e);
          }
        };

        widgetId = arcaptcha.render(container, {
          site_key: siteKey,
          size,
          lang: options.lang === 'en' ? 'en' : 'fa',
          theme: options.theme,
          callback: succeed,
          error_callback: fail,
          closed_callback: () => fail('closed', 'cancelled'),
          // The widget exists only from here on: invisible mode asks now.
          rendered_callback: () => {
            if (size === 'invisible') execute();
          },
          // Expired before we read it: in invisible mode just ask again.
          expired_callback: () => {
            if (size === 'invisible') execute();
          }
        });
      });
    },

    reset() {
      const id = widgetId;
      widgetId = undefined;
      if (id === undefined) return;
      // Before the widget is built there is nothing to reset, and no reset.
      try {
        window.arcaptcha?.reset?.(id);
      } catch {
        // A widget that cannot reset is dropped with its container anyway.
      }
    }
  };
};
